import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Divider,
  Tooltip,
  IconButton,
  Chip,
} from '@mui/material';
import { Refresh, TrendingUp, TrendingDown, TrendingFlat, Warning } from '@mui/icons-material';
import { ProductionAssumptionsData } from '../../types/widget';
import { apiService } from '../../services/api';

// Format currency
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
};

// Format variance with sign
const formatVariance = (variance: number, isCurrency: boolean = false): string => {
  const prefix = variance > 0 ? '+' : '';
  if (isCurrency) {
    return prefix + formatCurrency(variance);
  }
  return prefix + variance.toString();
};

// Get status color
const getStatusColor = (status: string): string => {
  switch (status) {
    case 'over':
      return '#F56565'; // red
    case 'under':
      return '#48BB78'; // green
    case 'on_target':
    case 'on_track':
    case 'tracking':
    case 'reserved':
    case 'baseline':
      return '#4299E1'; // blue
    case 'not_started':
      return '#A0AEC0'; // gray
    default:
      return '#A0AEC0';
  }
};

// Get variance icon
const getVarianceIcon = (variance: number, reverseColors: boolean = false) => {
  if (variance > 0) {
    return <TrendingUp sx={{ color: reverseColors ? '#48BB78' : '#F56565', fontSize: 20 }} />;
  }
  if (variance < 0) {
    return <TrendingDown sx={{ color: reverseColors ? '#F56565' : '#48BB78', fontSize: 20 }} />;
  }
  return <TrendingFlat sx={{ color: '#A0AEC0', fontSize: 20 }} />;
};

const ProductionAssumptionsWidget: React.FC<{ widgetId: string; title: string }> = ({
  title,
}) => {
  const [data, setData] = useState<ProductionAssumptionsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getProductionAssumptionsData();
      if (response.success && response.data) {
        setData(response.data);
      } else {
        throw new Error(response.error || 'Failed to fetch data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data) {
    return <Alert severity="info">No assumptions data available.</Alert>;
  }

  // Check if we have Option B data
  const hasOptionB = data.totalBudget && data.productionSpending;

  return (
    <Paper
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        backgroundColor: '#1A202C',
        overflow: 'auto',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data.productionTitle} • {data.baselineVersion}
          </Typography>
        </Box>
        <Tooltip title="Refresh Data">
          <IconButton onClick={fetchData} size="small">
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>
      <Divider sx={{ mb: 2 }} />

      {hasOptionB ? (
        /* Option B Layout: Multi-section display */
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={2}>
            
            {/* Section 1: Total Budget (Target vs Committed) */}
            {data.totalBudget && (
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, backgroundColor: '#2D3748', height: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {data.totalBudget.name}
                    </Typography>
                    <Chip 
                      label={data.totalBudget.status === 'over' ? 'OVER COMMITTED' : data.totalBudget.status.toUpperCase()}
                      size="small"
                      sx={{ 
                        backgroundColor: getStatusColor(data.totalBudget.status),
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.65rem',
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Target</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(data.totalBudget.target)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary">Committed</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(data.totalBudget.committed)}
                      </Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: getStatusColor(data.totalBudget.status),
                        fontWeight: 'bold',
                      }}
                    >
                      {formatVariance(data.totalBudget.variance, true)}
                    </Typography>
                    {data.totalBudget.variance > 0 && (
                      <Warning sx={{ color: '#F56565', fontSize: 20 }} />
                    )}
                    {getVarianceIcon(data.totalBudget.variance)}
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* Section 2: Production Spending (Hot Cost tracks this) */}
            {data.productionSpending && (
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, backgroundColor: '#2D3748', height: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {data.productionSpending.name}
                    </Typography>
                    <Chip 
                      label={data.productionSpending.status.toUpperCase()}
                      size="small"
                      sx={{ 
                        backgroundColor: getStatusColor(data.productionSpending.status),
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.65rem',
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Budget</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(data.productionSpending.budget)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary">Actuals</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(data.productionSpending.actuals)}
                      </Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Day {data.productionSpending.daysCompleted} of {data.productionSpending.totalDays}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ${data.productionSpending.costPerPage.toLocaleString()}/page
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* Section 3: Post-Production (Isolated) */}
            {data.postProduction && (
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, backgroundColor: '#2D3748', height: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {data.postProduction.name}
                    </Typography>
                    <Chip 
                      label={data.postProduction.status.toUpperCase()}
                      size="small"
                      sx={{ 
                        backgroundColor: getStatusColor(data.postProduction.status),
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.65rem',
                      }}
                    />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1 }}>
                    {formatCurrency(data.postProduction.budget)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Reserved • Not touched by Hot Cost
                  </Typography>
                </Paper>
              </Grid>
            )}

            {/* Section 4: Schedule */}
            {data.schedule && (
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, backgroundColor: '#2D3748', height: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {data.schedule.name}
                    </Typography>
                    <Chip 
                      label={data.schedule.status.replace('_', ' ').toUpperCase()}
                      size="small"
                      sx={{ 
                        backgroundColor: getStatusColor(data.schedule.status),
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.65rem',
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {data.schedule.original}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      days
                    </Typography>
                  </Box>
                  {data.schedule.variance !== 0 && (
                    <Typography 
                      variant="body2" 
                      sx={{ color: data.schedule.variance > 0 ? '#F56565' : '#48BB78' }}
                    >
                      {formatVariance(data.schedule.variance)} days
                    </Typography>
                  )}
                </Paper>
              </Grid>
            )}

            {/* Section 5: Creative (Script Pages) */}
            {data.creative && (
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, backgroundColor: '#2D3748', height: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {data.creative.name}
                    </Typography>
                    <Chip 
                      label={`${data.creative.scriptRevision} REV`}
                      size="small"
                      sx={{ 
                        backgroundColor: '#4299E1',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.65rem',
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Pages</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {data.creative.originalPagesFormatted}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary">Scenes</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {data.creative.originalScenes}
                      </Typography>
                    </Box>
                  </Box>
                  {data.creative.pagesShot > 0 && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" color="text.secondary">
                        Shot: {data.creative.pagesShotFormatted} pages
                      </Typography>
                    </>
                  )}
                </Paper>
              </Grid>
            )}
          </Grid>
        </Box>
      ) : (
        /* Legacy Layout: Simple 3-metric display */
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={2}>
            {/* Schedule */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, backgroundColor: '#2D3748', height: '100%' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {data.metrics.schedule.name}
                </Typography>
                <Box>
                  <Typography variant="caption" color="text.secondary">Original</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {data.metrics.schedule.original}
                  </Typography>
                </Box>
                <Box sx={{ my: 1 }}>
                  <Typography variant="caption" color="text.secondary">Current</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {data.metrics.schedule.current}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: data.metrics.schedule.variance > 0 ? '#F56565' : 
                             data.metrics.schedule.variance < 0 ? '#48BB78' : '#A0AEC0'
                    }}
                  >
                    {formatVariance(data.metrics.schedule.variance)}
                  </Typography>
                  {getVarianceIcon(data.metrics.schedule.variance)}
                </Box>
              </Paper>
            </Grid>

            {/* Budget */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, backgroundColor: '#2D3748', height: '100%' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {data.metrics.budget.name}
                </Typography>
                <Box>
                  <Typography variant="caption" color="text.secondary">Original</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(data.metrics.budget.original)}
                  </Typography>
                </Box>
                <Box sx={{ my: 1 }}>
                  <Typography variant="caption" color="text.secondary">Current</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(data.metrics.budget.current)}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: data.metrics.budget.variance > 0 ? '#F56565' : 
                             data.metrics.budget.variance < 0 ? '#48BB78' : '#A0AEC0'
                    }}
                  >
                    {formatVariance(data.metrics.budget.variance, true)}
                  </Typography>
                  {getVarianceIcon(data.metrics.budget.variance)}
                </Box>
              </Paper>
            </Grid>

            {/* Creative */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, backgroundColor: '#2D3748', height: '100%' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {data.metrics.creative.name}
                </Typography>
                <Box>
                  <Typography variant="caption" color="text.secondary">Original</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {data.metrics.creative.original}
                  </Typography>
                </Box>
                <Box sx={{ my: 1 }}>
                  <Typography variant="caption" color="text.secondary">Current</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {data.metrics.creative.current}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: data.metrics.creative.variance > 0 ? '#F56565' : 
                             data.metrics.creative.variance < 0 ? '#48BB78' : '#A0AEC0'
                    }}
                  >
                    {formatVariance(data.metrics.creative.variance)}
                  </Typography>
                  {getVarianceIcon(data.metrics.creative.variance, true)}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
    </Paper>
  );
};

export default ProductionAssumptionsWidget;
